import React, { useState, useEffect, useRef } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { showAlert } from '../../../../functions/show_alert';

export default function WizardEnterDetailsForm({ countryData, onDataChange, initialData }) {
    const [personName, setPersonName] = useState(initialData.personName || '');
    const [personImage, setPersonImage] = useState(initialData.personImage || null);
    const [personImageFileName, setPersonImageFileName] = useState(initialData.personImageFileName || '');
    const [businessName, setBusinessName] = useState(initialData.businessName || '');
    const [personFunction, setPersonFunction] = useState(initialData.personFunction || '');
    const [functionAtLocation, setFunctionAtLocation] = useState(initialData.functionAtLocation || '');
    const [functionImage, setFunctionImage] = useState(initialData.functionImage || null);
    const [functionImageFileName, setFunctionImageFileName] = useState(initialData.functionImageFileName || '');
    const onDataChangeRef = useRef(onDataChange);

    // Update ref when callback changes
    useEffect(() => {
        onDataChangeRef.current = onDataChange;
    }, [onDataChange]);

    // Only call onDataChange when actual data changes
    useEffect(() => {
        if (onDataChangeRef.current) {
            onDataChangeRef.current({
                personName,
                personImage,
                personImageFileName,
                businessName,
                personFunction,
                functionAtLocation,
                functionImage,
                functionImageFileName
            });
        }
    }, [personName, personImage, personImageFileName, businessName, personFunction, functionAtLocation, functionImage, functionImageFileName]);

    const compressImage = (file, maxWidth = 400, maxHeight = 400, maxSizeKB = 300) => {
        return new Promise((resolve, reject) => {
            // For PDF files, check size and reject if too large
            if (file.type === 'application/pdf') {
                if (file.size > maxSizeKB * 1024) {
                    reject(new Error(`PDF file size must be less than ${maxSizeKB}KB`));
                    return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                    const dataUrl = reader.result;
                    const base64Match = dataUrl.match(/^data:[^;]+;base64,(.+)$/);
                    resolve(base64Match ? base64Match[1] : dataUrl);
                };
                reader.readAsDataURL(file);
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Calculate new dimensions
                    if (width > height) {
                        if (width > maxWidth) {
                            height = (height * maxWidth) / width;
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxHeight) {
                            width = (width * maxHeight) / height;
                            height = maxHeight;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Try different quality levels to meet size requirement
                    let quality = 0.7;
                    let compressedDataUrl = canvas.toDataURL(file.type, quality);
                    let base64Data = compressedDataUrl.match(/^data:[^;]+;base64,(.+)$/)?.[1] || compressedDataUrl;
                    let sizeKB = (base64Data.length * 3) / 4 / 1024;

                    // Reduce quality if still too large
                    while (sizeKB > maxSizeKB && quality > 0.3) {
                        quality -= 0.1;
                        compressedDataUrl = canvas.toDataURL(file.type, quality);
                        base64Data = compressedDataUrl.match(/^data:[^;]+;base64,(.+)$/)?.[1] || compressedDataUrl;
                        sizeKB = (base64Data.length * 3) / 4 / 1024;
                    }

                    if (sizeKB > maxSizeKB) {
                        reject(new Error(`Image is too large even after compression (${sizeKB.toFixed(0)}KB). Please use a smaller image.`));
                        return;
                    }

                    resolve(base64Data);
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = reader.result;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    const handlePersonImageUpload = async (file) => {
        if (file.size > 10000000) {
            showAlert(false, 'The file size is more than 10 MB');
            return;
        }

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            showAlert(false, 'Only .jpg, .png, & .pdf files will be accepted');
            return;
        }

        try {
            const base64Data = await compressImage(file, 400, 400, 300);
            setPersonImage(base64Data);
            setPersonImageFileName(file.name);
        } catch (error) {
            console.error('Error processing file:', error);
            showAlert(false, error.message || 'Error processing file. Please try again.');
        }
    };

    const handleFunctionImageUpload = async (file) => {
        if (file.size > 10000000) {
            showAlert(false, 'The file size is more than 10 MB');
            return;
        }

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            showAlert(false, 'Only .jpg, .png, & .pdf files will be accepted');
            return;
        }

        try {
            const base64Data = await compressImage(file, 400, 400, 300);
            setFunctionImage(base64Data);
            setFunctionImageFileName(file.name);
        } catch (error) {
            console.error('Error processing file:', error);
            showAlert(false, error.message || 'Error processing file. Please try again.');
        }
    };

    const handleRemovePersonImage = () => {
        setPersonImage(null);
        setPersonImageFileName('');
    };

    const handleRemoveFunctionImage = () => {
        setFunctionImage(null);
        setFunctionImageFileName('');
    };

    return (
        <div className="wizard-enter-details-form">
            <div className="details-form-container">
                {/* Person Name */}
                <div className="form-field">
                    <label className="field-label">Person Name</label>
                    <input
                        type="text"
                        className="form-input"
                        placeholder="Enter person name"
                        value={personName}
                        onChange={(e) => setPersonName(e.target.value)}
                    />
                </div>

                {/* Person Image (optional) */}
                <div className="form-field">
                    <label className="field-label">Person Image (optional)</label>
                    <div
                        className="file-upload-area"
                        onClick={() => document.getElementById('person-image-input').click()}
                    >
                        <input
                            id="person-image-input"
                            type="file"
                            style={{ display: 'none' }}
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    handlePersonImageUpload(e.target.files[0]);
                                }
                            }}
                        />
                        <CloudUploadIcon className="upload-icon" />
                        <p className="upload-text">Upload the file here</p>
                        <p className="upload-hint">(Only .jpg, .png, & .pdf files will be accepted)</p>
                    </div>
                    {personImageFileName && (
                        <div className="uploaded-file-row">
                            <div className="uploaded-file-info">
                                <InsertDriveFileOutlinedIcon className="file-icon" />
                                <span className="uploaded-file-name">{personImageFileName}</span>
                            </div>
                            <DeleteIcon
                                className="remove-file-icon"
                                onClick={handleRemovePersonImage}
                            />
                        </div>
                    )}
                </div>

                {/* Business Name, Person Function, Function at location - in a row */}
                <div className="form-fields-row three-columns">
                    <div className="form-field">
                        <label className="field-label">Business Name (optional)</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Enter business name"
                            value={businessName}
                            onChange={(e) => setBusinessName(e.target.value)}
                        />
                    </div>
                    <div className="form-field">
                        <label className="field-label">Person Function</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Enter person function"
                            value={personFunction}
                            onChange={(e) => setPersonFunction(e.target.value)}
                        />
                    </div>
                    <div className="form-field">
                        <label className="field-label">Function at location</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Enter function at location"
                            value={functionAtLocation}
                            onChange={(e) => setFunctionAtLocation(e.target.value)}
                        />
                    </div>
                </div>

                {/* Function Image (optional) */}
                <div className="form-field">
                    <label className="field-label">Function Image (optional)</label>
                    <div
                        className="file-upload-area"
                        onClick={() => document.getElementById('function-image-input').click()}
                    >
                        <input
                            id="function-image-input"
                            type="file"
                            style={{ display: 'none' }}
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    handleFunctionImageUpload(e.target.files[0]);
                                }
                            }}
                        />
                        <CloudUploadIcon className="upload-icon" />
                        <p className="upload-text">Upload the file here</p>
                        <p className="upload-hint">(Only .jpg, .png, & .pdf files will be accepted)</p>
                    </div>
                    {functionImageFileName && (
                        <div className="uploaded-file-row">
                            <div className="uploaded-file-info">
                                <InsertDriveFileOutlinedIcon className="file-icon" />
                                <span className="uploaded-file-name">{functionImageFileName}</span>
                            </div>
                            <DeleteIcon
                                className="remove-file-icon"
                                onClick={handleRemoveFunctionImage}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
